<template>
  <naj-modal
    ref="subjectModal"
    :title="title"
    button-size="small"
    :button-outlined="false"
    :persistent="true"
    :close-button="true"
    @onClose="onClose"
  >
    <template slot-scope="{ closeNajModal }">
      <v-form
        ref="subjectFormRef"
        class="subject-form"
        @submit.prevent="formSubmit"
      >
        <div class="form-inputs-container">
          <v-row>
            <v-col>
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.caseRelatedRequest.interpretationJudgementRequest.judgementSectionLabel"
                :items="judgementCorrectionSections"
                :value.sync="subjectDetails.judgementSectionId"
                item-text="name"
                item-value="id"
                validates="required"
                @change="onJudgementSectionChange"
              ></naj-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div
                v-if="loading"
                class="loader-holder"
              >
                <naj-loader></naj-loader>
              </div>
              <div
                v-if="!loading"
                class="scrollable-div"
              >
                <p
                  ref="content"
                  @mouseup="highlightSelectedText"
                  v-html="textToHighlight"
                />
              </div>
            </v-col>
          </v-row>
          <v-row class="note-container">
            <v-col>
              <span class="sub-note">
                {{ local.caseRelatedRequest.interpretationJudgementRequest.cassationHighlightNote }}
              </span>
            </v-col>
          </v-row>
        </div>
        <div class="text-left form-actions">
          <naj-button
            outlined
            @click="onClose"
          >
            {{ local.common.cancel }}
          </naj-button>
          <naj-button
            type="submit"
            :loading="loading"
          >
            {{ local.common.save }}
          </naj-button>
        </div>
      </v-form>
    </template>
  </naj-modal>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import { judgementSection } from '@/helpers/enum';
const { mapState: mapLUTState } = createNamespacedHelpers('LookupsModule');
const { mapActions } = createNamespacedHelpers('LawsuitModule');
export default {
  name: 'InterpretationJudgementSubjects',
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    selectedJudgment: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      judgementSectionTextResult: null,
      textToHighlight: '',
      subjectDetails: {},
    };
  },
  computed: { ...mapLUTState(['judgementCorrectionSections']) },
  watch: {
    showModal(val) {
      if (val) {
        this.$refs.subjectModal.show();
        // copy value prop to local subjectDetails for both add and edit
        this.subjectDetails = { ...(this.value || {}) };
        // fetch judgment sections when modal opens (if not already fetched)
        this.loadJudgementSections();
      } else {
        this.$refs.subjectModal.close();
      }
    },
  },
  methods: {
    ...mapActions(['getJudgementSections']),
    sanitizeHTML(html) {
      if (!html) return '';
      // create a temporary div to convert HTML entities to safe text
      const temp = document.createElement('div');
      temp.textContent = html;
      // return the sanitized HTML (escapes any malicious scripts)
      return temp.innerHTML;
    },
    onClose() {
      // this.$refs.subjectModal.close();
      this.$emit('closeModal');
    },
    // load all judgment sections once when modal opens
    loadJudgementSections() {
      // skip if already loaded for this judgment
      if (
        this.judgementSectionTextResult &&
        this.judgementSectionTextResult.judgementCode === this.selectedJudgment.judgementCode
      ) {
        return;
      }

      this.loading = true;
      const params = {
        judgementCode: this.selectedJudgment.judgementCode,
        caseCode: this.formModel.case.caseCode,
      };

      this.getJudgementSections(params)
        .then((res) => {
          // cache the result with judgment code
          this.judgementSectionTextResult = {
            judgementCode: this.selectedJudgment.judgementCode,
            waqae: this.sanitizeHTML(res.waqae),
            reasons: this.sanitizeHTML(res.reasons),
            mantoq: this.sanitizeHTML(res.mantoq),
          };
        })
        .catch(() => {
          this.toaster.error(this.local.caseRelatedRequest.interpretationJudgementRequest.loadJudgementSectionsErrorMsg);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // switch between cached sections (no API call)
    onJudgementSectionChange() {
      if (!this.judgementSectionTextResult) {
        return;
      }

      // find the selected section name from the lookup
      const selectedSection = this.judgementCorrectionSections.find(
        section => section.id === this.subjectDetails.judgementSectionId
      );

      // update the section name in subjectDetails
      if (selectedSection) {
        this.subjectDetails.judgementSectionName = selectedSection.name;
      }

      // simply switch between cached sections based on selection
      if (this.subjectDetails.judgementSectionId == judgementSection.waqae) {
        this.textToHighlight = this.judgementSectionTextResult.waqae;
      } else if (this.subjectDetails.judgementSectionId == judgementSection.reasons) {
        this.textToHighlight = this.judgementSectionTextResult.reasons;
      } else if (this.subjectDetails.judgementSectionId == judgementSection.mantoq) {
        this.textToHighlight = this.judgementSectionTextResult.mantoq;
      }
    },
    highlightSelectedText() {
      const selection = window.getSelection();
      const selectedText = selection.toString();

      // ensure some text is selected
      if (!selectedText) return;

      // validate textToHighlight exists
      if (!this.textToHighlight) {
        this.toaster.error(this.local.caseRelatedRequest.interpretationJudgementRequest.selectJudgementErrorMessage);
        return;
      }

      // validate selection is within the judgment text
      if (!this.normalizeArabic(this.textToHighlight).includes(this.normalizeArabic(selectedText))) {
        this.clearHighlights();
        this.toaster.error(this.local.caseRelatedRequest.interpretationJudgementRequest.selectValidTextErrorMsg);
        return;
      }

      const content = this.$refs.content;
      // find the start and end positions
      let startOffset = 0;
      let endOffset = 0;
      let charCount = 0;

      const range = selection.getRangeAt(0);
      const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null, false);
      // traverse text nodes to find exact start and end indices
      while (walker.nextNode()) {
        const node = walker.currentNode;
        // if the range starts in this node
        if (node === range.startContainer) {
          startOffset = charCount + range.startOffset;
        }
        // if the range ends in this node
        if (node === range.endContainer) {
          endOffset = charCount + range.endOffset;
          break;
        }
        charCount += node.textContent.length;
      }

      // save the highlighting data to subjectDetails
      this.subjectDetails.judgementHighlightedText = selectedText;
      this.subjectDetails.startIndex = startOffset;
      this.subjectDetails.endIndex = endOffset;

      // highlight the text using the range API
      this.applyHighlight(range);
      // clear the selection
      selection.removeAllRanges();
    },

    applyHighlight(range) {
      // clear previous highlights
      this.clearHighlights();
      // wrap the selected text in a span with a yellow background
      const highlightSpan = document.createElement('span');
      highlightSpan.style.backgroundColor = 'cyan';
      highlightSpan.textContent = range.toString();
      range.deleteContents(); // remove selected text
      range.insertNode(highlightSpan); // insert highlighted text
    },

    clearHighlights() {
      const content = this.$refs.content;

      // traverse and replace all spans with their plain text content
      const walker = document.createTreeWalker(content, NodeFilter.SHOW_ELEMENT, null, false);

      const spans = [];
      while (walker.nextNode()) {
        if (walker.currentNode.tagName === 'SPAN') {
          spans.push(walker.currentNode);
        }
      }

      spans.forEach((span) => {
        const parent = span.parentNode;
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, span);
        }
        parent.removeChild(span);
      });
    },
    normalizeArabic(str) {
      return (
        str
          // remove diacritics
          .replace(/[\u064B-\u0652\u0670\u0640]/g, '')
          // normalize different forms of Alef
          .replace(/[إأآا]/g, 'ا')
          // normalize Teh Marbuta and Heh
          .replace(/ة/g, 'ه')
          // normalize different forms of Yeh
          .replace(/ي/g, 'ى')
          // remove extra spaces
          .replace(/\s+/g, ' ')
          .trim()
      );
    },
    formSubmit() {
      if (this.$refs.subjectFormRef) {
        this.$refs.subjectFormRef.$validator.validateAll().then((valid) => {
          if (valid) {
            if (
              this.subjectDetails.judgementHighlightedText === undefined ||
              this.subjectDetails.judgementHighlightedText.trim() === ''
            ) {
              this.toaster.error(this.local.caseRelatedRequest.interpretationJudgementRequest.selectJudgementErrorMessage);
              return;
            }
            this.$emit('addSubject', this.subjectDetails);
            this.onClose();
          }
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.attachment-name {
  align-items: center;
}
.download-attachment {
  margin-inline-start: 8px;
}
.section-title {
  font-weight: 600;
}
.scrollable-div {
  overflow-y: auto;
  max-height: 200px;
}
.sub-note {
  font-size: 14px;
  color: var(--app-secondary-color);
}
.note-container {
  margin-top: -12px;
}
</style>
